// ─────────────────────────────────────────────────────────────────────────────
// generateMigrationSQL — Produces idempotent PostgreSQL from a ScopeManifest.
//
// Architecture:
//   Phase 1 — INSERT scopes ON CONFLICT (tenant_id, scope_namespace, scope_key)
//   Phase 2 — UPDATE to wire parent references (IS DISTINCT FROM guard)
//   Phase 3 — DO $$ block:  one step per handler_key (correct linkage),
//             each outcome guarded by NOT EXISTS to prevent duplicates.
//
// The output SQL is safe to run multiple times against the same database.
// Follows the same idempotent pattern used by Terraform state, k8s manifests,
// and pg_dump restore scripts.
// ─────────────────────────────────────────────────────────────────────────────

import type { ScopeManifest, ManifestOutcome, ManifestHotStreamRow } from './scopeMigrateApi';

// ── SQL helpers ──────────────────────────────────────────────────────────────

/** Escape single quotes for SQL string literals. */
function esc(s: string): string {
  return s.replace(/'/g, "''");
}

/** Truncate long strings for SQL comments. */
function trunc(s: string, max = 60): string {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

/** Format ISO timestamp for SQL comments. */
function fmtTS(s: string | Date): string {
  if (!s) return 'unknown';
  const d = typeof s === 'string' ? s : (s as Date).toISOString();
  return d.replace('T', ' ').replace(/\.\d+Z?$/, '');
}

/** Convert a ManifestOutcome value to the correct SQL column + literal. */
function outcomeToSQL(o: ManifestOutcome): { col: string; val: string } {
  const kind = o.value_kind;
  const v = o.value;

  switch (kind) {
    case 'integer':
      return { col: 'v_integer', val: `${Number(v)}` };
    case 'floatpoint':
      return { col: 'v_floatpoint', val: `${Number(v)}` };
    case 'string':
      return { col: 'v_string', val: `'${esc(String(v))}'` };
    case 'boolean':
      return { col: 'v_boolean', val: v ? 'TRUE' : 'FALSE' };
    case 'jsonb':
      return {
        col: 'v_jsonb',
        val: `'${esc(typeof v === 'string' ? v : JSON.stringify(v))}'::jsonb`,
      };
    case 'timestamp':
      return { col: 'v_timestamp', val: `'${esc(String(v))}'::timestamptz` };
    default:
      // Auto-detect from JS type.
      if (typeof v === 'number')
        return { col: Number.isInteger(v) ? 'v_integer' : 'v_floatpoint', val: `${v}` };
      if (typeof v === 'boolean')
        return { col: 'v_boolean', val: v ? 'TRUE' : 'FALSE' };
      if (typeof v === 'string')
        return { col: 'v_string', val: `'${esc(v)}'` };
      return {
        col: 'v_jsonb',
        val: `'${esc(JSON.stringify(v ?? null))}'::jsonb`,
      };
  }
}

/** Convert a ManifestHotStreamRow value to the correct SQL column + literal. */
function hotStreamToSQL(o: ManifestHotStreamRow): { col: string; val: string } {
  const kind = o.value_kind;
  const v = o.value;

  switch (kind) {
    case 'integer':
      return { col: 'v_integer', val: `${Number(v)}` };
    case 'floatpoint':
      return { col: 'v_floatpoint', val: `${Number(v)}` };
    case 'string':
      return { col: 'v_string', val: `'${esc(String(v))}'` };
    case 'boolean':
      return { col: 'v_boolean', val: v ? 'TRUE' : 'FALSE' };
    case 'jsonb':
      return {
        col: 'v_jsonb',
        val: `'${esc(typeof v === 'string' ? v : JSON.stringify(v))}'::jsonb`,
      };
    case 'timestamp':
      return { col: 'v_timestamp', val: `'${esc(String(v))}'::timestamptz` };
    default:
      if (typeof v === 'number')
        return { col: Number.isInteger(v) ? 'v_integer' : 'v_floatpoint', val: `${v}` };
      if (typeof v === 'boolean')
        return { col: 'v_boolean', val: v ? 'TRUE' : 'FALSE' };
      if (typeof v === 'string')
        return { col: 'v_string', val: `'${esc(v)}'` };
      return {
        col: 'v_jsonb',
        val: `'${esc(JSON.stringify(v ?? null))}'::jsonb`,
      };
  }
}

/** Compute SHA-256 hex digest using Web Crypto API. */
async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── Main generator ──────────────────────────────────────────────────────────

/**
 * Generate idempotent PostgreSQL from a ScopeManifest.
 *
 * @param manifest  - The exported scope manifest (JSON).
 * @param targetTenantId - Override tenant_id for the target environment.
 * @returns          A self-contained .sql file contents.
 */
export async function generateMigrationSQL(
  manifest: ScopeManifest,
  targetTenantId?: number,
): Promise<string> {
  const tid = targetTenantId ?? manifest.tenant_id;
  const scopes = manifest.scopes ?? [];
  const outcomes = manifest.outcomes ?? [];
  const hotStream = manifest.hot_stream ?? [];
  const hotArchiveFiles = manifest.hot_archive_files ?? [];
  const hash = await sha256(JSON.stringify(manifest));

  const L: string[] = [];
  const w = (s = '') => L.push(s);

  // ── Header ──────────────────────────────────────────────────────

  w(`-- ═══════════════════════════════════════════════════════════════════════`);
  w(`-- PACMaster Scope Migration — Idempotent SQL`);
  w(`-- ═══════════════════════════════════════════════════════════════════════`);
  w(`--`);
  w(`-- Source env:     ${manifest.source_env || 'unknown'}`);
  w(`-- Exported at:    ${fmtTS(manifest.exported_at)}`);
  w(`-- Root scope:     ${manifest.root_scope.scope_namespace} / ${trunc(manifest.root_scope.scope_key, 50)}`);
  w(`-- Target tenant:  ${tid}`);
  w(`-- Scopes:         ${scopes.length}`);
  w(`-- Outcomes:       ${outcomes.length}`);
  w(`-- Hot stream:     ${hotStream.length} rows (live)`);
  w(`-- Hot archives:   ${hotArchiveFiles.length} GCS prefixes`);
  w(`-- Content hash:   sha256:${hash}`);
  w(`--`);
  w(`-- This SQL is idempotent — safe to run multiple times:`);
  w(`--   • Existing scopes matched by (tenant_id, scope_namespace, scope_key)`);
  w(`--   • ON CONFLICT touches updated_at (marks object as "reached")`);
  w(`--   • Parent references wired AFTER all scopes exist (SET CONSTRAINTS DEFERRED)`);
  w(`--   • Outcomes guarded by NOT EXISTS (handler_key + category + type)`);
  w(`--   • Each handler_key gets its own step for correct FK linkage`);
  w(`--`);
  w(`-- Usage:`);
  w(`--   psql -h <host> -U <user> -d <database> -f <this-file>.sql`);
  w(`--`);
  w(`-- GCP Cloud SQL:`);
  w(`--   cloud-sql-proxy <instance-connection-name> &`);
  w(`--   psql -h 127.0.0.1 -p 5432 -U <user> -d <db> -f <this-file>.sql`);
  w(`-- ═══════════════════════════════════════════════════════════════════════`);
  w();
  w(`BEGIN;`);
  w(`SET CONSTRAINTS ALL DEFERRED;`);
  w();

  // ── Phase 1: Scope upserts ──────────────────────────────────────

  if (scopes.length > 0) {
    w(`-- ─── Phase 1: Upsert scopes (${scopes.length}) ──────────────────────────────────`);
    w(`-- Creates missing scopes with fresh id_scope from the sequence.`);
    w(`-- Existing scopes get updated_at = NOW() (touch only — no data loss).`);
    w(`-- Parent references are wired in Phase 2, after ALL scopes exist.`);
    w();
    w(`INSERT INTO workflow.workflow_scopes`);
    w(`  (tenant_id, scope_namespace, scope_key, id_scope, created_at)`);
    w(`VALUES`);

    scopes.forEach((s, i) => {
      const comma = i < scopes.length - 1 ? ',' : '';
      const ts = typeof s.created_at === 'string' ? s.created_at : new Date(s.created_at).toISOString();
      w(`  (${tid}, '${esc(s.scope_namespace)}', '${esc(s.scope_key)}', nextval('workflow.workflow_scope_seq'), '${esc(ts)}'::timestamptz)${comma}`);
    });

    w(`ON CONFLICT (tenant_id, scope_namespace, scope_key) DO UPDATE SET`);
    w(`  updated_at = NOW();`);
    w();
  }

  // ── Phase 2: Wire parent references ─────────────────────────────

  const childScopes = scopes.filter((s) => s.parent_ref);
  if (childScopes.length > 0) {
    w(`-- ─── Phase 2: Wire parent references (${childScopes.length} children) ──────────────────`);
    w(`-- Resolves parent by (scope_namespace, scope_key) → id_scope.`);
    w(`-- IS DISTINCT FROM guard prevents no-op writes.`);
    w();
    w(`UPDATE workflow.workflow_scopes AS c`);
    w(`SET id_scope_father = p.id_scope`);
    w(`FROM (VALUES`);

    childScopes.forEach((s, i) => {
      const pr = s.parent_ref!;
      const comma = i < childScopes.length - 1 ? ',' : '';
      w(`  (${tid}::bigint, '${esc(s.scope_namespace)}'::text, '${esc(s.scope_key)}'::text, '${esc(pr.scope_namespace)}'::text, '${esc(pr.scope_key)}'::text)${comma}`);
    });

    w(`) AS v(tid, child_ns, child_key, parent_ns, parent_key)`);
    w(`JOIN workflow.workflow_scopes p`);
    w(`  ON p.tenant_id = v.tid`);
    w(`  AND p.scope_namespace = v.parent_ns`);
    w(`  AND p.scope_key = v.parent_key`);
    w(`WHERE c.tenant_id = v.tid`);
    w(`  AND c.scope_namespace = v.child_ns`);
    w(`  AND c.scope_key = v.child_key`);
    w(`  AND (c.id_scope_father IS DISTINCT FROM p.id_scope);`);
    w();
  }

  // ── Phase 3: Outcomes ───────────────────────────────────────────

  if (outcomes.length > 0) {
    // Group outcomes by handler_key → by scope.
    const byHandler = new Map<string, ManifestOutcome[]>();
    for (const o of outcomes) {
      const arr = byHandler.get(o.handler_key) ?? [];
      arr.push(o);
      byHandler.set(o.handler_key, arr);
    }

    w(`-- ─── Phase 3: Import outcomes (${outcomes.length} across ${byHandler.size} handler(s)) ──────`);
    w(`-- Creates a synthetic migration run with one step per handler_key`);
    w(`-- (correct FK linkage for handler_key on workflow_steps).`);
    w(`-- Each outcome is guarded by NOT EXISTS to prevent duplicates.`);
    w();
    w(`DO $$`);
    w(`DECLARE`);
    w(`  _run_id    BIGINT;`);
    w(`  _step_id   BIGINT;`);
    w(`  _exec_id   BIGINT;`);
    w(`  _scope_id  BIGINT;`);
    w(`  _father_id BIGINT;`);
    w(`BEGIN`);
    w(`  -- Synthetic migration run`);
    w(`  INSERT INTO workflow.workflow_runs`);
    w(`    (tenant_id, workflow_key, status, created_at, updated_at)`);
    w(`  VALUES (${tid}, '__scope_migration__', 2, NOW(), NOW())`);
    w(`  RETURNING run_id INTO _run_id;`);
    w();

    Array.from(byHandler.entries()).forEach(([handlerKey, handlerOutcomes]) => {
      const stepKey = `mig__${handlerKey}`.slice(0, 100);

      w(`  -- ── Handler: ${trunc(handlerKey, 70)} (${handlerOutcomes.length} outcomes) ──`);
      w(`  INSERT INTO workflow.workflow_steps`);
      w(`    (run_id, step_key, handler_key, status, created_at, updated_at)`);
      w(`  VALUES (_run_id, '${esc(stepKey)}', '${esc(handlerKey)}', 3, NOW(), NOW())`);
      w(`  RETURNING step_id INTO _step_id;`);
      w();
      w(`  INSERT INTO workflow.workflow_executions`);
      w(`    (step_id, attempt, status, started_at, finished_at)`);
      w(`  VALUES (_step_id, 1, 2, NOW(), NOW())`);
      w(`  RETURNING execution_id INTO _exec_id;`);
      w();

      // Group by scope for readability.
      const byScope = new Map<string, ManifestOutcome[]>();
      for (const o of handlerOutcomes) {
        const sk = `${o.scope_ref.scope_namespace}\x00${o.scope_ref.scope_key}`;
        const arr = byScope.get(sk) ?? [];
        arr.push(o);
        byScope.set(sk, arr);
      }

      Array.from(byScope.entries()).forEach(([scopeNK, scopeOutcomes]) => {
        const [ns, key] = scopeNK.split('\x00');
        const scopeData = scopes.find(
          (s) => s.scope_namespace === ns && s.scope_key === key,
        );

        w(`  -- Scope: ${ns} / ${trunc(key, 50)}`);
        w(`  SELECT id_scope INTO _scope_id FROM workflow.workflow_scopes`);
        w(`    WHERE tenant_id = ${tid}`);
        w(`      AND scope_namespace = '${esc(ns)}'`);
        w(`      AND scope_key = '${esc(key)}';`);

        if (scopeData?.parent_ref) {
          w(`  SELECT id_scope INTO _father_id FROM workflow.workflow_scopes`);
          w(`    WHERE tenant_id = ${tid}`);
          w(`      AND scope_namespace = '${esc(scopeData.parent_ref.scope_namespace)}'`);
          w(`      AND scope_key = '${esc(scopeData.parent_ref.scope_key)}';`);
        } else {
          w(`  _father_id := NULL;`);
        }
        w();

        for (const o of scopeOutcomes) {
          const { col, val } = outcomeToSQL(o);

          w(`  IF NOT EXISTS (`);
          w(`    SELECT 1 FROM workflow.workflow_outcomes eo`);
          w(`    JOIN workflow.workflow_steps es ON es.step_id = eo.step_id`);
          w(`    WHERE eo.tenant_id = ${tid}`);
          w(`      AND eo.id_scope = _scope_id`);
          w(`      AND es.handler_key = '${esc(handlerKey)}'`);
          w(`      AND eo.id_category = ${o.id_category}`);
          w(`      AND eo.id_type = ${o.id_type}`);
          w(`  ) THEN`);
          w(`    INSERT INTO workflow.workflow_outcomes`);
          w(`      (run_id, step_id, execution_id, tenant_id, id_scope, id_scope_father,`);
          w(`       id_category, id_type, ${col})`);
          w(`    VALUES`);
          w(`      (_run_id, _step_id, _exec_id, ${tid}, _scope_id, _father_id,`);
          w(`       ${o.id_category}, ${o.id_type}, ${val});`);
          w(`  END IF;`);
          w();
        }
      });
    });

    w(`  RAISE NOTICE 'PACMaster scope migration applied.  source=%, root=%/%, scopes=%, outcomes=%',`);
    w(`    '${esc(manifest.source_env || 'unknown')}',`);
    w(`    '${esc(manifest.root_scope.scope_namespace)}',`);
    w(`    '${esc(trunc(manifest.root_scope.scope_key, 40))}',`);
    w(`    ${scopes.length}, ${outcomes.length};`);
    w(`END $$;`);
    w();
  }

  // ── Phase 4: Hot stream rows ────────────────────────────────────

  if (hotStream.length > 0) {
    w(`-- ─── Phase 4: Insert hot_stream rows (${hotStream.length}) ──────────────────────────`);
    w(`-- Live hot_stream data from source environment.`);
    w(`-- Idempotent: guarded by NOT EXISTS on (scope_ns, scope_key, ts, id_category, id_type)`);
    w(`-- to prevent duplicates on re-run.`);
    w();

    // Group by scope_ns/scope_key for readability.
    const byScope = new Map<string, ManifestHotStreamRow[]>();
    for (const row of hotStream) {
      const key = `${row.scope_ns}\x00${row.scope_key}`;
      const arr = byScope.get(key) ?? [];
      arr.push(row);
      byScope.set(key, arr);
    }

    for (const [scopeNK, rows] of Array.from(byScope.entries())) {
      const [ns, key] = scopeNK.split('\x00');
      w(`-- Scope: ${ns} / ${trunc(key, 50)} (${rows.length} rows)`);

      for (const row of rows) {
        const { col, val } = hotStreamToSQL(row);
        const tsStr = typeof row.ts === 'string' ? row.ts : new Date(row.ts).toISOString();

        // Resolve id_scope from the scope_ref
        w(`INSERT INTO workflow.hot_stream`);
        w(`  (scope_ns, scope_key, tenant_id, id_scope, id_scope_father,`);
        w(`   id_category, id_type, ${col}, ts)`);
        w(`SELECT`);
        w(`  '${esc(row.scope_ns)}', '${esc(row.scope_key)}', ${tid},`);
        w(`  s.id_scope, s.id_scope_father,`);
        w(`  ${row.id_category}, ${row.id_type}, ${val},`);
        w(`  '${esc(tsStr)}'::timestamptz`);
        w(`FROM workflow.workflow_scopes s`);
        w(`WHERE s.tenant_id = ${tid}`);
        w(`  AND s.scope_namespace = '${esc(row.scope_ref.scope_namespace)}'`);
        w(`  AND s.scope_key = '${esc(row.scope_ref.scope_key)}'`);
        w(`  AND NOT EXISTS (`);
        w(`    SELECT 1 FROM workflow.hot_stream ex`);
        w(`    WHERE ex.scope_ns = '${esc(row.scope_ns)}'`);
        w(`      AND ex.scope_key = '${esc(row.scope_key)}'`);
        w(`      AND ex.tenant_id = ${tid}`);
        w(`      AND ex.ts = '${esc(tsStr)}'::timestamptz`);
        w(`      AND ex.id_category = ${row.id_category}`);
        w(`      AND ex.id_type = ${row.id_type}`);
        w(`  );`);
        w();
      }
    }

    // If there are archived Parquet files, emit a comment listing them.
    if (hotArchiveFiles.length > 0) {
      w(`-- ─── Hot archive GCS prefixes (${hotArchiveFiles.length}) ──────────────────────────`);
      w(`-- These Parquet files must be copied between GCS buckets separately.`);
      w(`-- Use: gsutil -m cp -r gs://SOURCE_BUCKET/<prefix>* gs://TARGET_BUCKET/<prefix>`);
      w(`--`);
      for (const f of hotArchiveFiles) {
        w(`--   ${f.rel_path}`);
      }
      w();
    }
  }

  // ── Footer ──────────────────────────────────────────────────────

  w(`COMMIT;`);
  w();
  w(`-- ═══════════════════════════════════════════════════════════════════════`);
  w(`-- Migration complete.`);
  w(`-- Content hash: sha256:${hash}`);
  w(`-- ═══════════════════════════════════════════════════════════════════════`);

  return L.join('\n');
}
